import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const PRIMARY = "#292E5C";
const SECONDARY = "#F7AA47";
const DARK_BLACK = "#000000";
const TEXT_DARK = "#171717";
const TEXT_PRIMARY = "#444444";
const TEXT_MUTED = "#808080";
const PLACEHOLDER = "#667085";
const BORDER = "#D8DBDF";
const ERROR = "#D92D20";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const PHOTO_SPECIFICATION = "Recommended Photo Keep face centered.";

const SKILL_OPTIONS = [
  "Chess Class",
  "Creative Writing",
  "Computer Programming",
  "Kids Public Speaking Courses",
  "Outdoor sports Activity",
  "Robotics Class",
  "Drama Classes",
  "YOGA Classes",
  "Curiosity Improvement Courses",
  "Music Courses",
  "Spoken English Courses",
  "Drawing And Painting",
  "Craftsman",
];

const EMPTY_FORM = {
  full_name: "",
  email: "",
  phone_number: "",
  linkedin: "",
  years_of_experience: "",
  skill: "",
  city: "",
  description: "",
  certificates: [],
  photo: null,
};

const TEXT_FIELDS = [
  {
    name: "full_name",
    label: "Full Name",
    placeholder: "Enter your full name",
    autoComplete: "name",
  },
  {
    name: "email",
    label: "Email",
    placeholder: "Enter your email",
    type: "email",
    autoComplete: "email",
  },
  {
    name: "phone_number",
    label: "Phone number",
    placeholder: "Enter your phone number",
    type: "tel",
    autoComplete: "tel",
    required: false,
  },
  {
    name: "linkedin",
    label: "Linked in",
    placeholder: "Enter Linked in Profile link",
    autoComplete: "url",
  },
  {
    name: "years_of_experience",
    label: "Year of experience",
    placeholder: "Enter Year of experience",
    type: "number",
    inputProps: { min: 0, max: 80 },
  },
  {
    name: "city",
    label: "City",
    placeholder: "Enter city",
    autoComplete: "address-level2",
  },
];

const isFileObject = (value) =>
  typeof File !== "undefined" && value instanceof File;

const createEmptyForm = () => ({
  ...EMPTY_FORM,
  certificates: [],
});

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();

  if (!url || /^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const getFileName = (file) => {
  if (!file) return "";

  if (isFileObject(file)) return file.name;

  if (typeof file === "object") {
    const fileUrl = file.file || file.url || file.certificate || "";
    return file.name || file.original_name || String(fileUrl).split("/").pop();
  }

  return String(file).split("/").pop();
};

const getCertificateUrl = (certificate) => {
  if (!certificate || isFileObject(certificate)) return "";

  if (typeof certificate === "object") {
    return normalizeMediaUrl(certificate.file || certificate.url || "");
  }

  return normalizeMediaUrl(certificate);
};

const normalizeExistingCertificate = (certificate, index) => {
  if (!certificate) return null;

  if (isFileObject(certificate)) return certificate;

  const fileUrl =
    typeof certificate === "object"
      ? certificate.file || certificate.url || certificate.certificate || ""
      : certificate;
  const name =
    typeof certificate === "object"
      ? certificate.name || certificate.original_name || getFileName(fileUrl)
      : getFileName(fileUrl);

  return {
    certificate_id:
      typeof certificate === "object" ? certificate.certificate_id || certificate.id : null,
    file: fileUrl,
    name: name || `Certificate ${index + 1}`,
    uploaded_at: typeof certificate === "object" ? certificate.uploaded_at || "" : "",
  };
};

const buildInitialForm = (initialValues) => {
  if (!initialValues) {
    return createEmptyForm();
  }

  const certificates = [];
  const existingCertificates = Array.isArray(initialValues.certificates)
    ? initialValues.certificates
    : [];

  existingCertificates.forEach((certificate, index) => {
    const normalizedCertificate = normalizeExistingCertificate(certificate, index);

    if (normalizedCertificate) {
      certificates.push(normalizedCertificate);
    }
  });

  if (!certificates.length && initialValues.certificate) {
    const normalizedCertificate = normalizeExistingCertificate(
      initialValues.certificate,
      0
    );

    if (normalizedCertificate) {
      certificates.push(normalizedCertificate);
    }
  }

  const initialSkill =
    initialValues.skill ||
    (typeof initialValues.skills === "string" ? initialValues.skills : "") ||
    "";

  return {
    ...createEmptyForm(),
    full_name: initialValues.full_name || initialValues.name || "",
    email: initialValues.email || "",
    phone_number: initialValues.phone_number || "",
    linkedin: initialValues.linkedin || "",
    years_of_experience:
      initialValues.years_of_experience === undefined ||
      initialValues.years_of_experience === null
        ? ""
        : String(initialValues.years_of_experience),
    skill: initialSkill,
    city: initialValues.city || "",
    description: initialValues.description || "",
    certificates,
    photo: initialValues.photo || null,
  };
};

const openFileInNewTab = (file) => {
  if (!isFileObject(file)) {
    const fileUrl = getCertificateUrl(file);

    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }

    return;
  }

  const fileUrl = URL.createObjectURL(file);
  window.open(fileUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(fileUrl), 30000);
};

const getInitials = (name) => {
  const initials = String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "S";
};

const normalizeUrl = (value) => {
  const trimmedValue = String(value || "").trim();

  if (!trimmedValue) return "";

  return /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const formatCertificateDate = (file) => {
  const date = isFileObject(file) ? file.lastModified : file?.uploaded_at;

  if (!date) return "";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
};

const validateForm = (form) => {
  const errors = {};

  [
    "full_name",
    "email",
    "linkedin",
    "years_of_experience",
    "city",
    "description",
  ].forEach(
    (field) => {
      if (!String(form[field] || "").trim()) {
        errors[field] = "This field is required.";
      }
    }
  );

  if (!String(form.skill || "").trim()) {
    errors.skill = "This field is required.";
  }

  if (form.email && !isValidEmail(form.email.trim())) {
    errors.email = "Enter a valid email.";
  }

  if (form.linkedin) {
    try {
      const parsedUrl = new URL(normalizeUrl(form.linkedin));

      if (!parsedUrl.hostname.toLowerCase().includes("linkedin.com")) {
        errors.linkedin = "Enter a valid LinkedIn URL.";
      }
    } catch {
      errors.linkedin = "Enter a valid LinkedIn URL.";
    }
  }

  const experience = Number(form.years_of_experience);

  if (
    form.years_of_experience !== "" &&
    (!Number.isInteger(experience) || experience < 0 || experience > 80)
  ) {
    errors.years_of_experience = "Enter a value between 0 and 80.";
  }

  if (!form.photo) {
    errors.photo = "Photo is required.";
  }

  if (!form.certificates.length) {
    errors.certificates = "At least one certificate is required.";
  }

  return errors;
};

const buildSkillProviderPayload = (form, { isEditMode = false } = {}) => {
  const payload = new FormData();
  const linkedin = normalizeUrl(form.linkedin);

  payload.append("full_name", form.full_name.trim());
  payload.append("email", form.email.trim());
  payload.append("years_of_experience", String(Number(form.years_of_experience)));
  payload.append("city", form.city.trim());
  payload.append("description", form.description.trim());
  payload.append("skill", form.skill.trim());
  payload.append("linkedin", linkedin);

  if (isFileObject(form.photo)) {
    payload.append("photo", form.photo);
  }

  const phoneNumber = form.phone_number.trim();

  if (phoneNumber) {
    payload.append("phone_number", phoneNumber);
  } else if (isEditMode) {
    payload.append("phone_number", "");
  }

  const newCertificates = form.certificates.filter(isFileObject);

  newCertificates.forEach((certificate) => {
    payload.append("certificates", certificate);
  });

  if (newCertificates.length) {
    payload.append("certificate", newCertificates[0]);
  }

  return payload;
};

const labelSx = {
  color: DARK_BLACK,
  fontSize: 15,
  fontFamily: "Poppins",
  fontWeight: 400,
  lineHeight: "20px",
  mb: "5px",
};

const requiredMarkSx = {
  color: ERROR,
  fontSize: 15,
  fontFamily: "Poppins",
  fontWeight: 400,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 42,
    bgcolor: "#FFFFFF",
    borderRadius: "5px",
    fontSize: 15,
    fontFamily: "Poppins",
    fontWeight: 400,
    lineHeight: "20px",

    "& fieldset": {
      borderColor: BORDER,
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: PRIMARY,
    },

    "&.Mui-focused fieldset": {
      borderColor: PRIMARY,
      borderWidth: "1px",
    },

    "&.Mui-error fieldset": {
      borderColor: ERROR,
    },

    "& input": {
      px: "12px",
      py: "10px",
      color: TEXT_DARK,

      "&::placeholder": {
        color: PLACEHOLDER,
        opacity: 1,
      },
    },
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: "2px",
    fontSize: 10,
    fontFamily: "Poppins",
    lineHeight: "12px",
  },
};

const descriptionSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    height: "auto",
    alignItems: "flex-start",

    "& textarea": {
      px: "12px",
      py: "10px",
      color: TEXT_DARK,

      "&::placeholder": {
        color: PLACEHOLDER,
        opacity: 1,
      },
    },
  },
};

const skillSelectSx = {
  minHeight: 42,
  bgcolor: "#FFFFFF",
  borderRadius: "5px",
  color: TEXT_DARK,
  fontSize: 14,
  fontFamily: "Poppins",
  fontWeight: 400,
  lineHeight: "20px",

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY,
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY,
    borderWidth: "1px",
  },

  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: ERROR,
  },

  "& .MuiSelect-select": {
    py: "10px",
    px: "12px",
  },
};

const skillMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "5px",
      boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.06)",
      maxHeight: 290,

      "& .MuiMenuItem-root": {
        minHeight: 24,
        px: "17px",
        py: "2px",
        color: "#111827",
        fontSize: 11,
        fontFamily: "Montserrat",
        fontWeight: 500,
        lineHeight: "20px",
      },

      "& .Mui-selected": {
        bgcolor: "#F2F2F2 !important",
      },
    },
  },
};

const AddSkillProviderModal = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  initialValues = null,
  mode = "create",
}) => {
  const photoInputRef = useRef(null);
  const certificateInputRef = useRef(null);

  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const isEditMode = mode === "edit";

  const photoPreview = useMemo(() => {
    if (!form.photo) return "";

    if (isFileObject(form.photo)) {
      return URL.createObjectURL(form.photo);
    }

    return normalizeMediaUrl(form.photo);
  }, [form.photo]);

  const skillOptions = useMemo(() => {
    if (form.skill && !SKILL_OPTIONS.includes(form.skill)) {
      return [form.skill, ...SKILL_OPTIONS];
    }

    return SKILL_OPTIONS;
  }, [form.skill]);

  useEffect(() => {
    return () => {
      if (isFileObject(form.photo) && photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [form.photo, photoPreview]);

  useEffect(() => {
    setForm(open ? buildInitialForm(initialValues) : createEmptyForm());
    setErrors({});
    setSubmitError("");
  }, [initialValues, open]);

  const setFieldValue = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setErrors((current) => ({
      ...current,
      [field]: "",
    }));

    setSubmitError("");
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      setFieldValue("photo", file);
    }

    event.target.value = "";
  };

  const handleCertificatesChange = (event) => {
    const selectedCertificates = Array.from(event.target.files || []);

    if (selectedCertificates.length) {
      setForm((current) => ({
        ...current,
        certificates: [...current.certificates, ...selectedCertificates],
      }));

      setSubmitError("");
    }

    event.target.value = "";
  };

  const handleRemoveCertificate = (certificateIndex) => {
    setForm((current) => ({
      ...current,
      certificates: current.certificates.filter(
        (certificate, index) => index !== certificateIndex
      ),
    }));

    setSubmitError("");
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm(form);
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    try {
      const result = await onSubmit(
        buildSkillProviderPayload(form, { isEditMode })
      );

      if (result?.ok) {
        onClose();
        return;
      }

      setSubmitError(
        result?.message ||
          `Unable to ${
            isEditMode ? "update" : "add"
          } skill provider right now.`
      );
    } catch (error) {
      setSubmitError(
        error?.message ||
          `Something went wrong while ${
            isEditMode ? "updating" : "adding"
          } skill provider.`
      );
    }
  };

  const displayName = form.full_name.trim() || "Skill Provider Name";
  const hasCertificates = form.certificates.length > 0;
  const dialogTitle = isEditMode ? "Edit Skill Provider" : "Add a Skill provider";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {
          width: 735,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "92vh",
          bgcolor: "#FFFFFF",
          borderRadius: "15px",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          minHeight: 72,
          px: "40px",
          py: "20px",
          bgcolor: "#292E5C",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: 20,
            fontFamily: "Montserrat",
            fontWeight: 700,
            wordBreak: "break-word",
          }}
        >
          {dialogTitle}
        </Typography>

        <IconButton
          type="button"
          aria-label={`Close ${dialogTitle.toLowerCase()} modal`}
          disabled={submitting}
          onClick={handleClose}
          sx={{
            width: 32,
            height: 32,
            color: "#FFFFFF",
            p: 0,

            "&:hover": {
              bgcolor: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
         px: "24px",
          pt: "18px",
          pb: "18px",
          mt:"20px",
          bgcolor: "#FFFFFF",
          overflowY: "auto",

          "&::-webkit-scrollbar": {
            width: 5,
          },

          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.28)",
            borderRadius: 10,
          },
        }}
      >
        {submitError ? (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: "8px",
              fontFamily: "Poppins",
            }}
          >
            {submitError}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "234px minmax(0, 1fr)",
            },
            gap: "10px",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              px: "20px",
              py: "15px",
              bgcolor: "rgba(80, 94, 85, 0.06)",
              borderRadius: "8px",
              border: `1px solid ${errors.photo ? ERROR : "#EAECF0"}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "15px",
              position: { md: "sticky" },
              top: { md: 0 },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: DARK_BLACK,
                fontSize: 16,
                fontFamily: "Montserrat",
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              Upload Your Photo <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <Box
              component="button"
              type="button"
              disabled={submitting}
              onClick={() => photoInputRef.current?.click()}
              sx={{
                width: 196,
                height: 196,
                p: 0,
                m: 0,
                border: "none",
                borderRadius: "50%",
                bgcolor: "rgba(41, 46, 92, 0.14)",
                cursor: submitting ? "not-allowed" : "pointer",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: "inherit",
              }}
            >
              <Avatar
                src={photoPreview || undefined}
                sx={{
                  width: 186,
                  height: 186,
                  bgcolor: "#D9D9D9",
                  color: PRIMARY,
                  fontSize: 42,
                  fontFamily: "Montserrat",
                  fontWeight: 700,
                }}
              >
                {!photoPreview ? getInitials(form.full_name) : null}
              </Avatar>

              <Box
                sx={{
                  width: 34,
                  height: 34,
                  position: "absolute",
                  right: 16,
                  bottom: 18,
                  bgcolor: SECONDARY,
                  borderRadius: "50%",
                  border: "1px solid #EAECF0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhotoCameraOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: "#FFFFFF",
                  }}
                />
              </Box>
            </Box>

            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: ERROR,
                fontSize: 11,
                fontFamily: "Poppins",
                fontWeight: 500,
                lineHeight: "15px",
              }}
            >
              {PHOTO_SPECIFICATION}
            </Typography>

            <input
              ref={photoInputRef}
              hidden
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
            />

            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: TEXT_DARK,
                  fontSize: 16,
                  fontFamily: "Montserrat",
                  fontWeight: 700,
                  lineHeight: "18px",
                  wordBreak: "break-word",
                }}
              >
                {displayName}
              </Typography>

              {errors.photo ? (
                <Typography
                  sx={{
                    mt: 0.75,
                    textAlign: "center",
                    color: ERROR,
                    fontSize: 12,
                    fontFamily: "Poppins",
                  }}
                >
                  {errors.photo}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {TEXT_FIELDS.map((field) => (
              <Box key={field.name}>
                <Typography sx={labelSx}>
                  {field.label}
                  {field.required === false ? null : (
                    <Box component="span" sx={requiredMarkSx}> *</Box>
                  )}
                </Typography>

                <TextField
                  fullWidth
                  required={field.required !== false}
                  type={field.type || "text"}
                  value={form[field.name]}
                  disabled={submitting}
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                  error={Boolean(errors[field.name])}
                  helperText={errors[field.name] || ""}
                  inputProps={field.inputProps}
                  onChange={(event) =>
                    setFieldValue(field.name, event.target.value)
                  }
                  sx={inputSx}
                />
              </Box>
            ))}

            <Box>
              <Typography sx={labelSx}>
                Description <Box component="span" sx={requiredMarkSx}>*</Box>
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={3}
                value={form.description}
                disabled={submitting}
                placeholder="Type here . . ."
                error={Boolean(errors.description)}
                helperText={errors.description || ""}
                onChange={(event) =>
                  setFieldValue("description", event.target.value)
                }
                sx={descriptionSx}
              />
            </Box>

            <Box>
              <Typography sx={labelSx}>
                Skills: <Box component="span" sx={requiredMarkSx}>*</Box>
              </Typography>

              <Select
                fullWidth
                displayEmpty
                value={form.skill}
                disabled={submitting}
                error={Boolean(errors.skill)}
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setFieldValue("skill", event.target.value)}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Typography
                        component="span"
                        sx={{
                          color: PLACEHOLDER,
                          fontSize: 15,
                          fontFamily: "Poppins",
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                      >
                        Select a skill
                      </Typography>
                    );
                  }

                  return value;
                }}
                MenuProps={skillMenuProps}
                sx={skillSelectSx}
              >
                {skillOptions.map((skill) => (
                  <MenuItem key={skill} value={skill}>
                    {skill}
                  </MenuItem>
                ))}
              </Select>

              {errors.skill ? (
                <Typography
                  sx={{
                    mt: "2px",
                    color: ERROR,
                    fontSize: 10,
                    fontFamily: "Poppins",
                    lineHeight: "12px",
                  }}
                >
                  {errors.skill}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography sx={labelSx}>
                Upload Certificate <Box component="span" sx={requiredMarkSx}>*</Box>
              </Typography>

              <Box sx={{ pt: "10px", pb: hasCertificates ? "10px" : 0 }}>
                <Box
                  component="button"
                  type="button"
                  disabled={submitting}
                  onClick={() => certificateInputRef.current?.click()}
                  sx={{
                    width: "100%",
                    py: "20px",
                    px: 2,
                    bgcolor: "rgba(239, 239, 239, 0.50)",
                    borderRadius: "10px",
                    border: `0.5px dashed ${
                      errors.certificates ? ERROR : TEXT_MUTED
                    }`,
                    cursor: submitting ? "not-allowed" : "pointer",
                    font: "inherit",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",

                    "&:hover": {
                      borderColor: PRIMARY,
                    },
                  }}
                >
                  <CloudUploadOutlinedIcon
                    sx={{
                      fontSize: 38,
                      color: errors.certificates ? ERROR : SECONDARY,
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "center",
                        color: errors.certificates ? ERROR : TEXT_PRIMARY,
                        fontSize: 14,
                        fontFamily: "Montserrat",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {errors.certificates || "Please upload document(s)"}
                    </Typography>

                    <Box
                      sx={{
                        width: 180,
                        p: "10px",
                        bgcolor: PRIMARY,
                        borderRadius: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: 14,
                          fontFamily: "Montserrat",
                          fontWeight: 500,
                          wordBreak: "break-word",
                        }}
                      >
                        Upload
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <input
                ref={certificateInputRef}
                hidden
                type="file"
                multiple
                accept=".pdf,.doc,.docx,image/*"
                onChange={handleCertificatesChange}
              />

              {hasCertificates ? (
                <Box
                  sx={{
                    p: "10px",
                    bgcolor: "rgba(239, 239, 239, 0.50)",
                    borderRadius: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      py: "5px",
                      borderBottom: "0.5px solid rgba(128, 128, 128, 0.50)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: TEXT_PRIMARY,
                        fontSize: 15,
                        fontFamily: "DM Sans",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      Uploaded Certificate
                    </Typography>
                  </Box>

                  {form.certificates.map((certificate, index) => (
                    <Box
                      key={`${getFileName(certificate)}-${certificate.lastModified || index}`}
                      sx={{
                        minHeight: 55,
                        px: "20px",
                        py: "10px",
                        bgcolor: "rgba(247, 170, 71, 0.10)",
                        borderTopLeftRadius: index === 0 ? "10px" : 0,
                        borderTopRightRadius: index === 0 ? "10px" : 0,
                        borderBottom: "0.5px solid rgba(128, 128, 128, 0.18)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <PictureAsPdfIcon
                        sx={{
                          width: 35,
                          height: 35,
                          color: "#EF5350",
                          flexShrink: 0,
                        }}
                      />

                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: "3px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: TEXT_PRIMARY,
                            fontSize: 15,
                            fontFamily: "DM Sans",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {getFileName(certificate)}
                        </Typography>

                        <Typography
                          sx={{
                            color: TEXT_MUTED,
                            fontSize: 12,
                            fontFamily: "DM Sans",
                            fontWeight: 400,
                            wordBreak: "break-word",
                          }}
                        >
                          {formatCertificateDate(certificate)}
                        </Typography>
                      </Box>

                      <IconButton
                        type="button"
                        aria-label={`Open certificate ${index + 1}`}
                        onClick={() => openFileInNewTab(certificate)}
                        sx={{
                          width: 24,
                          height: 24,
                          p: 0,
                          color: TEXT_PRIMARY,
                          flexShrink: 0,
                        }}
                      >
                        <KeyboardArrowRightIcon sx={{ fontSize: 24 }} />
                      </IconButton>

                      {isFileObject(certificate) ? (
                        <IconButton
                          type="button"
                          aria-label={`Remove certificate ${index + 1}`}
                          disabled={submitting}
                          onClick={() => handleRemoveCertificate(index)}
                          sx={{
                            width: 24,
                            height: 24,
                            p: 0,
                            color: "#ED2224",
                            flexShrink: 0,
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: "40px",
          pt: "20px",
          pb: "30px",
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          disabled={submitting}
          onClick={handleClose}
          variant="outlined"
          sx={{
            px: "30px",
            py: "8px",
            minWidth: 108,
            borderRadius: "7px",
            borderColor: "#B5B5B5",
            color: "#B5B5B5",
            fontSize: 16,
            fontFamily: "Poppins",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",

            "&:hover": {
              borderColor: "#B5B5B5",
              bgcolor: "rgba(181,181,181,0.06)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={submitting}
          variant="contained"
          sx={{
            width: 108,
            px: "30px",
            py: "8px",
            borderRadius: "7px",
            bgcolor: SECONDARY,
            color: "#FDFDFD",
            fontSize: 16,
            fontFamily: "Poppins",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",
            boxShadow: "none",

            "&:hover": {
              bgcolor: "#E99A32",
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              bgcolor: "rgba(247, 170, 71, 0.55)",
              color: "#FFFFFF",
            },
          }}
        >
          {submitting
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
              ? "Save"
              : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddSkillProviderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  initialValues: PropTypes.object,
  mode: PropTypes.oneOf(["create", "edit"]),
};

export default AddSkillProviderModal;
